import { FC, useMemo } from 'react';
import { ResponsiveBar } from '@nivo/bar';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useNivoChartTheme } from '@/hooks/useNivoChartTheme';
import { evaluateComponentReadiness } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/evaluateComponentReadiness';
import {
    getReportComponentDatasetQueryStateKey,
    useReportData,
    useReportQuery,
} from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/hook';
import { chartTransformers } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/transformers';
import { useDatasetStore } from '@/stores/database/aliases/datasets/store';
import { ReportComponent, ReportComponentType } from '@/types';
import { ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import {
    averageNumericValues,
    filterZeroBarRows,
    formatCompactChartValue,
    getAverageMarker,
    getBarBottomLegend,
    getCartesianChartMargin,
    getChartFormatting,
    humanizeChartLabel,
    truncateChartLabel,
} from '../chartPresentation';
import CustomNivoChartTooltip from '../CustomNivoChartTooltip';
import { useNivoReportFormatting } from '../useNivoReportFormatting';

interface BarChartProps {
    component: ReportComponent;
    numerator: number;
    denominator: number;
}

const BarChart: FC<BarChartProps> = ({ component, numerator, denominator }): JSX.Element => {
    const { token } = useAntDesignTheme();
    const { nivoChartTheme, colorsArray } = useNivoChartTheme();

    const config = component.configuration as ReportComponentConfig;
    const formatting = getChartFormatting(config);

    const query = useReportQuery(component);
    const { data: rawData } = useReportData(component);
    const { financialValueDisplay, formatMetricValue, formatDimensionValue, getMetricLabel, getDimensionLabel } =
        useNivoReportFormatting(component);

    const transformer = chartTransformers[component.type as ReportComponentType];
    const formatValue = (value: number | string | null | undefined) => formatMetricValue(value);

    const realData = useMemo(() => {
        const transformed = transformer(rawData, query);

        // Only sort if it's a date field
        const order = query.dimensions[0]?.sortBy ?? 'asc';

        // return [...transformed].sort((a, b) => {
        //     const diff = new Date(a.date).getTime() - new Date(b.date).getTime();

        //     return order === 'asc' ? diff : -diff;
        // });

        const dim = query.dimensions[0]?.field;

        return [...transformed].sort((a, b) => {
            const aVal = a[dim];
            const bVal = b[dim];

            const diff = new Date(aVal).getTime() - new Date(bVal).getTime();

            return order === 'asc' ? diff : -diff;
        });
    }, [rawData, query, transformer]);

    const { isReady } = evaluateComponentReadiness(component);
    const queryStateKey = getReportComponentDatasetQueryStateKey(component);
    const isLoading = useDatasetStore((state) => state.componentQueryState[queryStateKey]?.loading);

    const isNotReady = !isReady;
    const isEmpty = isReady && !isLoading && !realData.length;

    const dummyData = [
        {
            country: 'AD',
            'hot dog': 130,
            'hot dogColor': 'hsl(133, 70%, 50%)',
            burger: 152,
            burgerColor: 'hsl(293, 70%, 50%)',
            sandwich: 183,
            sandwichColor: 'hsl(313, 70%, 50%)',
            kebab: 199,
            kebabColor: 'hsl(127, 70%, 50%)',
            fries: 72,
            friesColor: 'hsl(210, 70%, 50%)',
            donut: 77,
            donutColor: 'hsl(216, 70%, 50%)',
        },
        {
            country: 'AE',
            'hot dog': 60,
            'hot dogColor': 'hsl(170, 70%, 50%)',
            burger: 83,
            burgerColor: 'hsl(98, 70%, 50%)',
            sandwich: 159,
            sandwichColor: 'hsl(342, 70%, 50%)',
            kebab: 179,
            kebabColor: 'hsl(225, 70%, 50%)',
            fries: 112,
            friesColor: 'hsl(13, 70%, 50%)',
            donut: 197,
            donutColor: 'hsl(209, 70%, 50%)',
        },
        {
            country: 'AF',
            'hot dog': 11,
            'hot dogColor': 'hsl(120, 70%, 50%)',
            burger: 173,
            burgerColor: 'hsl(25, 70%, 50%)',
            sandwich: 164,
            sandwichColor: 'hsl(62, 70%, 50%)',
            kebab: 30,
            kebabColor: 'hsl(252, 70%, 50%)',
            fries: 61,
            friesColor: 'hsl(186, 70%, 50%)',
            donut: 136,
            donutColor: 'hsl(52, 70%, 50%)',
        },
        {
            country: 'AG',
            'hot dog': 122,
            'hot dogColor': 'hsl(273, 70%, 50%)',
            burger: 20,
            burgerColor: 'hsl(43, 70%, 50%)',
            sandwich: 198,
            sandwichColor: 'hsl(194, 70%, 50%)',
            kebab: 96,
            kebabColor: 'hsl(129, 70%, 50%)',
            fries: 138,
            friesColor: 'hsl(323, 70%, 50%)',
            donut: 175,
            donutColor: 'hsl(260, 70%, 50%)',
        },
        {
            country: 'AI',
            'hot dog': 61,
            'hot dogColor': 'hsl(146, 70%, 50%)',
            burger: 198,
            burgerColor: 'hsl(203, 70%, 50%)',
            sandwich: 150,
            sandwichColor: 'hsl(58, 70%, 50%)',
            kebab: 186,
            kebabColor: 'hsl(248, 70%, 50%)',
            fries: 167,
            friesColor: 'hsl(266, 70%, 50%)',
            donut: 9,
            donutColor: 'hsl(242, 70%, 50%)',
        },
        {
            country: 'AL',
            'hot dog': 13,
            'hot dogColor': 'hsl(123, 70%, 50%)',
            burger: 170,
            burgerColor: 'hsl(310, 70%, 50%)',
            sandwich: 97,
            sandwichColor: 'hsl(174, 70%, 50%)',
            kebab: 48,
            kebabColor: 'hsl(44, 70%, 50%)',
            fries: 57,
            friesColor: 'hsl(126, 70%, 50%)',
            donut: 195,
            donutColor: 'hsl(24, 70%, 50%)',
        },
        {
            country: 'AM',
            'hot dog': 183,
            'hot dogColor': 'hsl(69, 70%, 50%)',
            burger: 127,
            burgerColor: 'hsl(119, 70%, 50%)',
            sandwich: 196,
            sandwichColor: 'hsl(350, 70%, 50%)',
            kebab: 98,
            kebabColor: 'hsl(17, 70%, 50%)',
            fries: 82,
            friesColor: 'hsl(81, 70%, 50%)',
            donut: 28,
            donutColor: 'hsl(360, 70%, 50%)',
        },
    ];

    const dummyKeys = ['hot dog', 'burger', 'sandwich', 'kebab', 'fries', 'donut'];
    const metricKeys = query.metrics.map((metric) => metric.field).filter(Boolean);
    const keys = isNotReady ? dummyKeys : metricKeys;
    const indexBy = isNotReady ? 'country' : (query.dimensions[0]?.field ?? 'category');
    const data = filterZeroBarRows(isNotReady ? dummyData : isEmpty ? [] : realData, keys, formatting.showZeroValues);
    const metricLegend = getMetricLabel();
    const dimensionLegend = getDimensionLabel();
    const horizontal = config?.formatting?.layout === 'horizontal';
    const margin = getCartesianChartMargin({
        horizontal,
        showAxisLabels: formatting.showAxisLabels,
        showLegend: formatting.showLegend,
    });
    const labelByKey = Object.fromEntries(
        query.metrics.map((metric, index) => [metric.field, humanizeChartLabel(metric.label ?? getMetricLabel(index))]),
    );
    const averageValue = averageNumericValues(data.flatMap((row) => keys.map((key) => row[key])));
    const averageMarkers = formatting.showAverageLine
        ? getAverageMarker({
              axis: horizontal ? 'x' : 'y',
              color: token.colorTextTertiary,
              financialValueDisplay,
              formatValue,
              value: averageValue,
          })
        : [];

    return (
        <div
            style={{
                aspectRatio: `${numerator}/${denominator}`,
                height: '100%',
                minHeight: 0,
                width: '100%',
            }}
        >
            <ResponsiveBar
                animateOnMount={false}
                data={data}
                keys={keys}
                indexBy={indexBy}
                margin={margin}
                padding={0.3}
                groupMode={config?.formatting?.groupBy ?? 'grouped'}
                layout={horizontal ? 'horizontal' : 'vertical'}
                valueScale={{ type: 'linear' }}
                valueFormat={(value) => formatValue(value)}
                indexScale={{ type: 'band', round: true }}
                colors={colorsArray}
                theme={nivoChartTheme}
                borderColor={{
                    from: 'color',
                    modifiers: [['darker', 1]],
                }}
                axisTop={null}
                axisRight={null}
                axisBottom={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: formatting.showAxisLabels ? (horizontal ? metricLegend : dimensionLegend) : undefined,
                    legendPosition: 'middle',
                    legendOffset: 40,
                    truncateTickAt: 18,
                    format: (value) =>
                        horizontal && Number.isFinite(Number(value))
                            ? formatCompactChartValue(value, formatValue, financialValueDisplay)
                            : truncateChartLabel(formatDimensionValue(value), 18),
                }}
                axisLeft={{
                    tickSize: 5,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: formatting.showAxisLabels ? (horizontal ? dimensionLegend : metricLegend) : undefined,
                    legendPosition: 'middle',
                    legendOffset: horizontal ? -88 : -56,
                    truncateTickAt: 16,
                    format: (value) =>
                        !horizontal && Number.isFinite(Number(value))
                            ? formatCompactChartValue(value, formatValue, financialValueDisplay)
                            : truncateChartLabel(formatDimensionValue(value), 18),
                }}
                labelSkipWidth={12}
                labelSkipHeight={12}
                labelFormat={(value) => formatCompactChartValue(value, formatValue, financialValueDisplay)}
                labelTextColor={token.colorTextLightSolid}
                role='application'
                ariaLabel='Bar Chart'
                borderRadius={2}
                innerPadding={4}
                markers={averageMarkers}
                tooltip={(datum) => (
                    <CustomNivoChartTooltip
                        bar={datum}
                        formatValue={(value) => formatValue(value)}
                        labelByKey={labelByKey}
                    />
                )}
                // barAriaLabel={(e: any) => e.id + ': ' + e.formattedValue + ' in country: ' + e.indexValue}
                legends={formatting.showLegend ? getBarBottomLegend({ dataFrom: 'keys', translateY: 64 }) : []}
            />
        </div>
    );
};

export default BarChart;
